'use client'

import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { handleCMS } from "@/(front-desk)/store"
import { LoadingIcon, Pattern2 } from "@/(front-desk)/utils/patterns"
import { reportError } from "@/(front-desk)/utils/functions"
import { useTranslation } from "@/(front-desk)/hooks/useTranslation"
import locales from "@/(front-desk)/(pages)/information/information.i18n"
import ActivityDetail from "@/(front-desk)/(pages)/information/components/ActivityDetail"
import LanguageBtn from "@/(front-desk)/components/LanguageBtn"
import ImageLoader from "@/(front-desk)/components/ImageLoader"
import Transition from "@/(front-desk)/components/Transition"
import Marquee from "@/(front-desk)/components/Marquee"
import classNames from "classnames"
import dayjs from "dayjs"
import axios from "axios"
import studentExhibition from "@/(front-desk)/assets/images/information/activities/student-exhibition.png"
import gameExhibition from "@/(front-desk)/assets/images/information/activities/game-exhibition.png"
import sportsExhibition from "@/(front-desk)/assets/images/information/activities/sports-exhibition.png"
import effectsExhibition from "@/(front-desk)/assets/images/information/activities/effects-exhibition.png"
import soNetExhibition from "@/(front-desk)/assets/images/information/activities/so_net-exhibition.png"
import filmExhibition from "@/(front-desk)/assets/images/information/activities/film-exhibition.png"
import comingSoon from "@/(front-desk)/assets/images/background/coming-soon.png"


const specialExhibitionImages = {
    game: {
        size: '3rem',
        src: gameExhibition.src,
    },
    student: {
        size: '7.25rem',
        src: studentExhibition.src,
    },
    sports: {
        size: '3.8rem',
        src: sportsExhibition.src,
    },
    effects: {
        size: '3.4rem',
        src: effectsExhibition.src,
    },
    so_net: {
        size: '2.5rem',
        src: soNetExhibition.src,
    },
    film: {
        size: '3.25rem',
        src: filmExhibition.src,
    },
}

export default function informationActivities() {

    const dispatch = useDispatch()
    const { language, translations } = useTranslation(locales)
    const infoEvents = useSelector((state) => state.CMS).infoEvents[language]
    const content = translations.activities
    const categories = infoEvents ? [...new Map(infoEvents.map(e => [JSON.stringify(e.category), e.category]))?.values()] : null
    const global = useSelector((state) => state.global)
    const [ active, setActive ] = useState(null)

    useEffect(() => {
        if (language && !infoEvents) {
            axios.get(`/api/v1/cms/${language}/events?time_table=true&year=${global.currentCompeteYear}`)
            .then((res) => {
                // console.log(res.config?.url, res.data.body)
                dispatch(handleCMS.actions.setInfoEvents({
                    language: language,
                    data: res.data.body
                }))
            })
            .catch((err) => {
                // console.log(err.config?.url, err)
                reportError(err, true)
            })
        }
    }, [language])

    return (
        <>
        <title>展覽活動資訊 - 放視大賞</title>
        <main className="information-activities flex flex-col justify-between">
            <div className="page-banner">
                <div className="page-banner-content flex flex-wrap gap-4 justify-between items-end">
                    <div className="">
                        <h1 className="title">Activities</h1>
                        <h2 className="sub-title">展覽活動資訊</h2>
                    </div>
                    <LanguageBtn />
                </div>
            </div>
            <Transition transitionKey={!infoEvents}>
                { !infoEvents
                    ?
                    <div className="flex-center">
                        <LoadingIcon />
                    </div>
                    :
                    <>
                    { infoEvents.length === 0
                        ?
                        <div id="main" className="flex-center pt-[4rem] pb-[6rem]">
                            <img className="w-full" src={comingSoon.src} />
                            <div className="absolute text-center">
                                <div className="text-[1.875rem] text-main-700 font-bold">
                                    2026 放視大賞
                                </div>
                                <div className="text-[3rem] text-main-600 font-bold">
                                    即將公開
                                </div>
                            </div>
                        </div>
                        :
                        <div id="main">
                            <div className="activities-content flex flex-col gap-[8rem] pt-[4rem] mb-[10rem]">
                                { categories.map((category) => (
                                    // console.log('category', category),
                                    <div key={`category-${category.shortcut}`}>
                                        <div className="flex flex-col gap-2 mb-[4rem]">
                                            <Pattern2 className="w-[1.5rem] rotate-12" />
                                            <div className="text-black-500 font-light">
                                                {category.subtitle}
                                            </div>
                                            <div className="text-main text-[1.875rem] font-bold">
                                                {category.title}
                                            </div>
                                            <div className="text-white text-[1.125rem]">
                                                <span className="font-bold">{content?.location_title}｜</span>
                                                {infoEvents.find((c) => c.category?.shortcut === category?.shortcut)?.location}
                                            </div>
                                            <div className="text-black-500 font-light whitespace-pre-wrap">
                                                {category.description_md}
                                            </div>
                                        </div>
                                        <div className="item-group !items-stretch">
                                            { infoEvents.filter((e) => !e.only_time_table && (e.category.shortcut === category.shortcut)).map((event) => (
                                                // console.log('event', event),
                                                <div
                                                    key={`event-${event.id}`}
                                                    className="item flex flex-col bg-black-900 rounded-[1.25rem] cursor-pointer overflow-hidden"
                                                    onClick={() => {setActive(event)}}
                                                >
                                                    { event.cover &&
                                                        <ImageLoader
                                                            containerClassName="!h-[17.5rem]"
                                                            src={event.cover}
                                                        />
                                                    }
                                                    <div className="h-full flex flex-col items-end justify-between gap-[1.5rem] p-[1.5rem]">
                                                        <div className="w-full">
                                                            <div className="title">
                                                                {event.name}
                                                            </div>
                                                            <div className="text-black-500">
                                                                {dayjs(event.start_time).format('YYYY.MM.DD ')}
                                                                {translations.weekday?.[dayjs(event.start_time).format('d')]} |
                                                                {dayjs(event.start_time).format(' HH:mm')}-{dayjs(event.end_time).format('HH:mm')}
                                                            </div>
                                                        </div>
                                                        { event.signup_link
                                                            ?
                                                            <a
                                                                className={classNames('btn btn-outline-white btn-round', {
                                                                    'brightness-[0.85] opacity-[0.5] pointer-events-none': dayjs(event.end_time).isBefore(dayjs())
                                                                })}
                                                                href={event.signup_link}
                                                                target="_blank"
                                                                title={`${content?.signup?.link_title}-${event.name}`}
                                                            >{content?.signup?.link_name}</a>
                                                            :
                                                            <div
                                                                className="text-black-300"
                                                            >{content?.signup?.no_required}</div>
                                                        }
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                <div>
                                    <div className="flex flex-col gap-2 mb-[4rem]">
                                        <Pattern2 className="w-[1.5rem] rotate-12" />
                                        <div className="text-main text-[1.875rem] font-bold">
                                            {content?.special_exhibition?.name}
                                        </div>
                                        <div className="text-white text-[1.125rem]">
                                            <span className="font-bold">{content?.location_title}｜</span>
                                            {content?.special_exhibition?.location}
                                        </div>
                                    </div>
                                    <div className="columns-1 lg:columns-2 gap-10">
                                        { Object.keys(content?.special_exhibition?.categories).map((category) => {
                                            const categoryData = content?.special_exhibition?.categories?.[category]
                                            return (
                                                <div
                                                    key={`category-${category}`}
                                                    className="w-full flex flex-col gap-6 p-8 bg-black-900 rounded-[1.25rem] mb-10 break-inside-avoid"
                                                >
                                                    <img
                                                        src={specialExhibitionImages[category].src}
                                                        style={{
                                                            width: specialExhibitionImages[category]?.size,
                                                        }}
                                                    />
                                                    <div className="text-[1.5rem] text-white font-semibold">
                                                        {categoryData?.name}
                                                    </div>
                                                    <div className="text-black-500 whitespace-pre-wrap">
                                                        {categoryData?.description}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    </>
                }
            </Transition>
            <Marquee />
            { active &&
                <ActivityDetail info={active} setInfo={setActive} />
            }
        </main>
        </>
    )
}
